/**
 * Per-tenant ship-to restrictions (PLA2-688).
 *
 * Some retailers can't legally ship live plants into certain states (state
 * plant-quarantine rules). The designer checks the consumer's ZIP against this
 * list at the ZIP step — before any design is generated — and shows a redirect
 * screen instead of letting them build a plan they can't order.
 *
 * Client-safe: plain data + pure functions, no Node APIs.
 *
 * `excludedShipStates` mirrors the retailer's Shopify shipping restrictions
 * (PLA2-819) — keep the two lists identical. The design page enforces it on
 * ZIP submit and on every later step (restored sessions, resume links), so a
 * restricted-state user can never select or cart plants.
 */

export interface TenantShippingConfig {
  /** Display name used in the redirect copy ("Back to …"). */
  retailerName: string;
  /** USPS state codes this retailer cannot ship to. */
  excludedShipStates: readonly string[];
  /** Retailer storefront homepage for the "Back to …" link. */
  storefrontUrl: string;
  /** `utm_source` for the hand-off to Plantista's own designer. */
  utmSource: string;
}

/** The Perennial Farm (Glen Arm, MD). */
export const PERENNIAL_FARM_RETAILER_ID =
  "743e60da-2f82-4a0d-bd36-16d290e70c0c";

export const TENANT_SHIPPING: Record<string, TenantShippingConfig> = {
  [PERENNIAL_FARM_RETAILER_ID]: {
    retailerName: "The Perennial Farm",
    excludedShipStates: [
      "AK",
      "AZ",
      "CA",
      "CO",
      "HI",
      "ID",
      "MT",
      "NV",
      "OR",
      "UT",
      "WA",
    ],
    storefrontUrl: "https://www.perennialfarm.com",
    utmSource: "perennialfarm",
  },
};

/** Plantista's own (D2C) designer — where restricted-state users are sent. */
export const PLANTISTA_DESIGNER_URL = "https://designer.plantista.ai";

// USPS 3-digit ZIP prefix ranges → state. Inclusive [from, to]. Military
// (AA/AE/AP) and territories resolve to their own codes, which are never on an
// exclusion list. Prefix-level resolution is off only for a handful of
// border-straddling 5-digit ZIPs; unknown prefixes return null (fail open).
const ZIP3_RANGES: ReadonlyArray<readonly [number, number, string]> = [
  [5, 5, "NY"],
  [6, 7, "PR"],
  [8, 8, "VI"],
  [9, 9, "PR"],
  [10, 27, "MA"],
  [28, 29, "RI"],
  [30, 38, "NH"],
  [39, 49, "ME"],
  [50, 54, "VT"],
  [55, 55, "MA"],
  [56, 59, "VT"],
  [60, 69, "CT"],
  [70, 89, "NJ"],
  [90, 99, "AE"],
  [100, 149, "NY"],
  [150, 196, "PA"],
  [197, 199, "DE"],
  [200, 200, "DC"],
  [201, 201, "VA"],
  [202, 205, "DC"],
  [206, 219, "MD"],
  [220, 246, "VA"],
  [247, 268, "WV"],
  [270, 289, "NC"],
  [290, 299, "SC"],
  [300, 319, "GA"],
  [320, 339, "FL"],
  [340, 340, "AA"],
  [341, 349, "FL"],
  [350, 369, "AL"],
  [370, 385, "TN"],
  [386, 397, "MS"],
  [398, 399, "GA"],
  [400, 427, "KY"],
  [430, 459, "OH"],
  [460, 479, "IN"],
  [480, 499, "MI"],
  [500, 528, "IA"],
  [530, 549, "WI"],
  [550, 567, "MN"],
  [569, 569, "DC"],
  [570, 577, "SD"],
  [580, 588, "ND"],
  [590, 599, "MT"],
  [600, 629, "IL"],
  [630, 658, "MO"],
  [660, 679, "KS"],
  [680, 693, "NE"],
  [700, 714, "LA"],
  [716, 729, "AR"],
  [730, 732, "OK"],
  [733, 733, "TX"],
  [734, 749, "OK"],
  [750, 799, "TX"],
  [800, 816, "CO"],
  [820, 831, "WY"],
  [832, 838, "ID"],
  [840, 847, "UT"],
  [850, 865, "AZ"],
  [870, 884, "NM"],
  [885, 885, "TX"],
  [889, 898, "NV"],
  [900, 961, "CA"],
  [962, 966, "AP"],
  [967, 968, "HI"],
  [969, 969, "GU"],
  [970, 979, "OR"],
  [980, 994, "WA"],
  [995, 999, "AK"],
];

const STATE_NAMES: Record<string, string> = {
  AK: "Alaska",
  AZ: "Arizona",
  CA: "California",
  CO: "Colorado",
  HI: "Hawaii",
  ID: "Idaho",
  MT: "Montana",
  NV: "Nevada",
  OR: "Oregon",
  UT: "Utah",
  WA: "Washington",
};

/** Resolve a US ZIP to its USPS state code, or null if it can't be resolved. */
export function zipToState(zipcode: string): string | null {
  const m = /^(\d{3})\d{2}(?:-?\d{4})?$/.exec(zipcode.trim());
  if (!m) return null;
  const prefix = Number(m[1]);
  for (const [from, to, state] of ZIP3_RANGES) {
    if (prefix >= from && prefix <= to) return state;
  }
  return null;
}

export interface ShipRestriction {
  state: string;
  stateName: string;
  config: TenantShippingConfig;
}

/**
 * Returns the restriction when `retailerId` can't ship to `zipcode`'s state,
 * otherwise null (including unknown tenants and unresolvable ZIPs).
 */
export function getShipRestriction(
  retailerId: string | null | undefined,
  zipcode: string | null | undefined
): ShipRestriction | null {
  if (!retailerId || !zipcode) return null;
  const config = TENANT_SHIPPING[retailerId];
  if (!config) return null;
  const state = zipToState(zipcode);
  if (!state || !config.excludedShipStates.includes(state)) return null;
  return { state, stateName: STATE_NAMES[state] ?? state, config };
}

/** Plantista designer URL for a restricted-state user, ZIP prefilled + UTM-tagged. */
export function plantistaRedirectUrl(
  restriction: ShipRestriction,
  zipcode: string
): string {
  const params = new URLSearchParams({
    d2c: "1",
    zip: zipcode,
    utm_source: restriction.config.utmSource,
    utm_medium: "ship_redirect",
  });
  return `${PLANTISTA_DESIGNER_URL}/design?${params}`;
}
